"use client"
import { useTheme } from "@mui/material/styles"
import { FC } from "react"
import { useTranslation } from "react-i18next"

import styles from "./PostFooter.module.scss"

import { Button } from "@/components/common/button"
import { Grid, GridRow } from "@/components/common/grid"
import { Link } from "@/components/common/link"

import { create } from "@/helpers/bem"
import {
  Category,
  NextPreviousPostQuery,
} from "@/types/generated/strapi/graphql"

const bem = create(styles, "PostFooter")

type PickOnly<T, K extends keyof T> = Pick<T, K> & {
  [P in Exclude<keyof T, K>]?: never
}

export type PostFooterData = NextPreviousPostQuery

export type PostFooterProps = {
  data?: PostFooterData
  category?: PickOnly<Category, "name" | "slug">
  publishedAt?: string
  updatedAt?: string
  updatedAtPlain?: string
}

export const PostFooter: FC<PostFooterProps> = ({
  data,
  category,
  updatedAt,
  updatedAtPlain,
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const theme = useTheme()
  const themeModifier = {
    [theme.palette.mode]: true,
  }
  const nextArticle = data?.nextArticle?.data
  const prevArticle = data?.previousArticle?.data
  return (
    <footer className={bem(undefined, themeModifier)}>
      {(category || updatedAt) && (
        <div className={bem("meta", themeModifier)}>
          {category?.name && (
            <p>
              {t("pages.views.post.footer.category")}{" "}
              <Link
                aria-label={`${t("accessibility.link.ariaLabel")} ${category.name}`}
                href={`/${locale}/blog/${category?.slug}`}
              >
                <span itemProp="articleSection">{category.name}</span>
              </Link>
            </p>
          )}
          {updatedAt && (
            <p>
              {t("pages.views.post.footer.updatedAt")}{" "}
              <time
                className={bem("meta__date", themeModifier)}
                dateTime={updatedAtPlain}
                itemProp="dateModified"
              >
                {updatedAt}
              </time>
            </p>
          )}
        </div>
      )}
      {category?.slug && (
        <Grid>
          <GridRow display="flex" justifyContent="flex-start" xs={6}>
            {prevArticle && prevArticle?.length > 0 && (
              <Button
                aria-label={t("pages.views.post.footer.buttons.previous")}
                className={bem("button")}
                color="primary"
                title={t("pages.views.post.footer.buttons.previous")}
                variant="outlined"
                redirect={{
                  href: `/${locale}/blog/${category?.slug}/${prevArticle?.[0]?.attributes?.slug}`,
                }}
              />
            )}
          </GridRow>
          <GridRow display="flex" justifyContent="flex-end" xs={6}>
            {nextArticle && nextArticle?.length > 0 && (
              <Button
                aria-label={t("pages.views.post.footer.buttons.next")}
                className={bem("button")}
                color="primary"
                title={t("pages.views.post.footer.buttons.next")}
                variant="contained"
                redirect={{
                  href: `/${locale}/blog/${category?.slug}/${nextArticle?.[0]?.attributes?.slug}`,
                }}
              />
            )}
          </GridRow>
        </Grid>
      )}
    </footer>
  )
}
